import { useState, useEffect } from 'react';
import type { ILeadsService } from '../services/leads';
import type { LeadDetailsData } from '../types/api';

export type UseLeadPreloadResult = {
  leadDetails: LeadDetailsData | null;
  isLoading: boolean;
  error: string | null;
};

const ACCEPTED_APPLICATION_STATUSES = new Set(['accepted', 'accepted_tier2']);

export const isAcceptedApplicationStatus = (status: string): boolean => {
  return ACCEPTED_APPLICATION_STATUSES.has(status);
};

export const readLeadUniqueIdFromUrl = (): string | null => {
  return new URLSearchParams(window.location.search).get('useruniqueId');
};

export const useLeadPreload = (leadsService: ILeadsService): UseLeadPreloadResult => {
  const [leadDetails, setLeadDetails] = useState<LeadDetailsData | null>(null);
  const [isLoading, setIsLoading] = useState(() => readLeadUniqueIdFromUrl() !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const leadUniqueId = readLeadUniqueIdFromUrl();
    if (!leadUniqueId) return;

    let cancelled = false;

    leadsService
      .getLeadDetails(leadUniqueId)
      .then(data => {
        if (cancelled) return;
        setLeadDetails(data);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load lead details for useruniqueId:', err);
        setError('Unable to load your application status.');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadsService]);

  return { leadDetails, isLoading, error };
};
